import { useEffect, useRef, useState } from "react";
import { useInView, useMotionValue, useTransform, animate } from "framer-motion";

/**
 * Animated counter that respects prefers-reduced-motion and starts on scroll.
 * Accepts strings like "12,400+", "94%", "212" — preserves suffix and separators.
 */
export default function CountUp({ value, duration = 1.8 }) {
  const match = String(value).match(/^([\d,]+)(.*)$/);
  const target = match ? parseInt(match[1].replace(/,/g, ""), 10) : null;
  const suffix = match ? match[2] : "";
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => `${Math.round(v).toLocaleString()}${suffix}`);
  const [text, setText] = useState(target ? `0${suffix}` : String(value));

  useEffect(() => {
    if (target === null || !inView) return;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setText(`${target.toLocaleString()}${suffix}`);
      return;
    }
    const controls = animate(mv, target, { duration, ease: [0.16, 1, 0.3, 1] });
    const unsub = display.on("change", (v) => setText(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [inView, target, mv, display, duration, suffix]);

  if (target === null) return <span ref={ref}>{value}</span>;
  return <span ref={ref}>{text}</span>;
}
